var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, } from "@mui/material";
import { Serializer } from "survey-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AUTOCOMPLETE_VALUES } from "../../utils/autocomplete-values";
import FormStepsContent from "../../components/shared/FormStepsContent";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { useWebgate } from "@xvia/webgate-connect";
import { useAccessTokenRef } from "../../hooks/useAccessTokenRef";
import { FORM_STEPS } from "../../constants/forms.constants";
import { buildBaseFormPayload, focusFormInputAfterValidationError, formHasPages, getApiErrorMessage, getFormValidationError, defineAutoCompleteOptions, forceSyncJsonEditorTab, } from "../../utils/forms.functions";
import { getDefaultSlugFromSlugs, slugifyTitle } from "../../utils/helpers";
import FormStepper from "./FormStepper";
var EditorLayout = function (_a) {
    var config = _a.config;
    var slugParam = useParams().slug;
    var navigate = useNavigate();
    var userInfo = useWebgate().userInfo;
    var _b = useAccessTokenRef(), accessTokenRef = _b.accessTokenRef, hasAccessToken = _b.hasAccessToken;
    defineAutoCompleteOptions(Serializer, AUTOCOMPLETE_VALUES);
    var creator = useMemo(function () { return config.createCreator(); }, [config]);
    var _c = useState(0), activeStep = _c[0], setActiveStep = _c[1];
    var _d = useState({
        title: "",
        slug: "",
        description: null,
        callbackURL: null,
        callbackIncludeUserAuth: false,
        authRequired: config.defaultAuthRequired,
    }), preFormInfo = _d[0], setPreFormInfo = _d[1];
    var _e = useState(false), open = _e[0], setOpen = _e[1];
    var _f = useState(true), loading = _f[0], setLoading = _f[1];
    var _g = useState(null), loadError = _g[0], setLoadError = _g[1];
    var _h = useState(false), saving = _h[0], setSaving = _h[1];
    var formTitleInputRef = useRef(null);
    var formSlugInputRef = useRef(null);
    var isLastPage = useMemo(function () { return activeStep === FORM_STEPS.length - 1; }, [activeStep]);
    var finalFormPortalUrl = useMemo(function () {
        var formattedSlug = slugifyTitle(preFormInfo.slug);
        return "".concat(process.env.PORTAL_URL).concat(config.urlPrefix).concat(formattedSlug);
    }, [preFormInfo.slug, config.urlPrefix]);
    var handleActionsAfterErrorDialogClose = useCallback(function () {
        var _a, _b;
        if (activeStep !== 1)
            return;
        if (!((_a = preFormInfo.title) === null || _a === void 0 ? void 0 : _a.trim())) {
            focusFormInputAfterValidationError(formTitleInputRef);
        }
        else if (!((_b = preFormInfo.slug) === null || _b === void 0 ? void 0 : _b.trim())) {
            focusFormInputAfterValidationError(formSlugInputRef);
        }
    }, [preFormInfo.title, preFormInfo.slug, activeStep]);
    var _j = useErrorDialog(handleActionsAfterErrorDialogClose), errorData = _j.errorData, showError = _j.showError, closeError = _j.closeError;
    useEffect(function () {
        var token = accessTokenRef.current;
        if (!slugParam || !token) {
            setLoading(false);
            if (!slugParam) {
                setLoadError("Identificador do formulário não informado.");
            }
            else {
                setLoadError("Não foi possível carregar o formulário para edição.");
            }
            return;
        }
        var cancelled = false;
        setLoading(true);
        setLoadError(null);
        config
            .fetchApi(slugParam, token)
            .then(function (res) {
            var _a, _b, _c, _d, _e;
            if (cancelled)
                return;
            var form = res.data;
            creator.text = JSON.stringify((_a = form.schema) !== null && _a !== void 0 ? _a : {});
            setPreFormInfo({
                title: (_b = form.title) !== null && _b !== void 0 ? _b : "",
                slug: getDefaultSlugFromSlugs(form.slugs),
                description: (_c = form.description) !== null && _c !== void 0 ? _c : null,
                callbackURL: (_d = form.callbackURL) !== null && _d !== void 0 ? _d : null,
                callbackIncludeUserAuth: !!form.callbackIncludeUserAuth,
                authRequired: (_e = form === null || form === void 0 ? void 0 : form.authRequired) !== null && _e !== void 0 ? _e : config.defaultAuthRequired,
            });
        })
            .catch(function (err) {
            var _a, _b, _c;
            if (cancelled)
                return;
            setLoadError((_c = (_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : "Não foi possível carregar o formulário para edição.");
        })
            .finally(function () {
            if (!cancelled)
                setLoading(false);
        });
        return function () {
            cancelled = true;
        };
    }, [hasAccessToken, slugParam, creator, config, accessTokenRef]);
    var handleNext = useCallback(function () {
        forceSyncJsonEditorTab(creator);
        if (!formHasPages(creator)) {
            showError("O formulário deve ter pelo menos um campo !");
            return;
        }
        setActiveStep(function (prevActiveStep) { return prevActiveStep + 1; });
    }, [creator, showError]);
    var handleBack = useCallback(function () {
        setActiveStep(function (prevActiveStep) { return prevActiveStep - 1; });
    }, []);
    var handleCancel = useCallback(function () {
        navigate(config.listPath);
    }, [navigate, config.listPath]);
    var submitUpdate = useCallback(function () {
        var validationError = getFormValidationError(preFormInfo, creator);
        if (validationError) {
            showError(validationError);
            return;
        }
        var body = __assign(__assign({}, buildBaseFormPayload(preFormInfo, creator)), { userUpdated: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.preferred_username) || (userInfo === null || userInfo === void 0 ? void 0 : userInfo.name) || "Sem dados" });
        setSaving(true);
        config
            .updateApi(slugParam, body, accessTokenRef.current)
            .then(function () {
            setOpen(true);
        })
            .catch(function (err) {
            console.error("Erro ao atualizar o formulário", err);
            showError(getApiErrorMessage(err, "Erro ao atualizar o formulário. Tente novamente."));
        })
            .finally(function () {
            setSaving(false);
        });
    }, [
        preFormInfo,
        creator,
        accessTokenRef,
        slugParam,
        userInfo,
        config,
        showError,
    ]);
    var handleClose = useCallback(function () {
        setOpen(false);
        navigate(config.listPath);
    }, [navigate, config.listPath]);
    var handleOpenPublishedForm = useCallback(function () {
        window.open(finalFormPortalUrl, "_blank", "noopener,noreferrer");
        handleClose();
    }, [finalFormPortalUrl, handleClose]);
    if (loading) {
        return (_jsx(Grid, __assign({ container: true, sx: { maxWidth: "1920px", margin: "0 auto" } }, { children: _jsx(Grid, __assign({ item: true, xs: 12 }, { children: loadError ? (_jsx(Alert, __assign({ severity: "error", sx: { mt: 2 } }, { children: loadError }))) : (_jsx(Box, __assign({ sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 400,
                    } }, { children: _jsx(CircularProgress, { size: 40, thickness: 4 }) }))) })) })));
    }
    return (_jsxs(Grid, __assign({ container: true, sx: { maxWidth: "1920px", margin: "0 auto" } }, { children: [_jsx(FormStepper, { activeStep: activeStep }), _jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsx(FormStepsContent, { step: activeStep, creator: creator, preFormInfo: preFormInfo, setPreFormInfo: setPreFormInfo, autoSyncSlugFromTitle: false, formTitleInputRef: formTitleInputRef, formSlugInputRef: formSlugInputRef, urlPrefix: config.urlPrefix }) })), _jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsxs(Box, __assign({ sx: {
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 3,
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    } }, { children: [_jsxs(Box, { children: [activeStep === 0 && (_jsx(Button, __assign({ variant: "contained", color: "error", onClick: handleCancel, disabled: saving }, { children: "Cancelar" }))), activeStep > 0 && (_jsx(Button, __assign({ disabled: activeStep === 0 || saving, onClick: handleBack, variant: "outlined" }, { children: "Voltar" })))] }), _jsx(Box, { children: isLastPage ? (_jsx(Button, __assign({ variant: "contained", color: "primary", onClick: submitUpdate, disabled: saving }, { children: saving ? "Salvando…" : "Salvar alterações" }))) : (_jsx(Button, __assign({ variant: "contained", color: "primary", onClick: handleNext, disabled: saving }, { children: "Continuar" }))) })] })) })), _jsxs(Dialog, __assign({ open: open, onClose: handleClose }, { children: [_jsx(DialogTitle, { children: "Formul\u00E1rio atualizado" }), _jsxs(DialogContent, { children: ["As altera\u00E7\u00F5es foram salvas com sucesso. Voc\u00EA pode voltar \u00E0 listagem ou continuar editando mais tarde.", _jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("br", {}), "O formul\u00E1rio se encontra em: ", _jsx("br", {}), _jsx(Box, __assign({ component: "a", href: finalFormPortalUrl, target: "_blank", rel: "noopener noreferrer", onClick: function (e) {
                                            e.preventDefault();
                                            handleOpenPublishedForm();
                                        }, sx: {
                                            fontWeight: "bold",
                                            color: "primary.main",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            "&:hover": { textDecoration: "underline" },
                                        } }, { children: finalFormPortalUrl }))] })] }), _jsxs(DialogActions, { children: [_jsx(Button, __assign({ onClick: handleOpenPublishedForm, color: "primary", variant: "contained" }, { children: "Abrir formul\u00E1rio" })), _jsx(Button, __assign({ onClick: handleClose, color: "primary", variant: "outlined" }, { children: "Voltar \u00E0 listagem" }))] })] })), _jsxs(Dialog, __assign({ open: errorData.open, onClose: closeError }, { children: [_jsx(DialogTitle, __assign({ color: "blue" }, { children: "Aten\u00E7\u00E3o" })), _jsx(DialogContent, { children: errorData.message }), _jsx(DialogActions, { children: _jsx(Button, __assign({ onClick: closeError, color: "primary" }, { children: "Fechar" })) })] }))] })));
};
export default EditorLayout;
